import { useEffect } from 'react';
import type { ChatMessageView, ChatReadEvent } from '@tg/shared';
import { CHAT_TOAST_PREVIEW } from '@tg/shared';
import { paths } from '@/app/paths';
import { useLocale } from '@/i18n/useLocale';
import { useSocketEvent } from '@/socket/socket';
import { selectInGame, useScreen } from '@/stores/screen';
import { useSession } from '@/stores/session';
import { useToasts } from '@/stores/toasts';
import { applyRead, loadBlocked, loadConversations, loadThread, receive, resetChat, retryNetworkFailed, useChat } from './store';

/** like the friend-request toast: long enough to reach the link; in a game — short and quiet */
export const MESSAGE_TOAST_MS = 15_000;
export const MESSAGE_TOAST_GAME_MS = 6_000;

/** the first CHAT_TOAST_PREVIEW characters (code points: an emoji is never cut in half) */
export function preview(text: string): string {
  const cps = Array.from(text.replace(/\s+/g, ' ').trim());
  return cps.length > CHAT_TOAST_PREVIEW ? `${cps.slice(0, CHAT_TOAST_PREVIEW).join('')}…` : cps.join('');
}

/**
 * Chat events for the whole app (spec 2026-09-30 §1–2):
 * - `chat:message` → the store (list, open thread, optimistic bubble) and the toast «Message from {name}: …»
 *   with a link to the conversation — not for my own messages (tab sync) and not when that conversation is open;
 *   during a game the toast is short, and toasts never make a sound;
 * - `chat:read` → read marks on my messages;
 * - the connection is back → failed sends are retried with the same clientId;
 * - sign out → everything is forgotten.
 */
export function ChatListener() {
  const status = useSession((st) => st.status);
  const connection = useSession((st) => st.connection);
  const l = useLocale();

  useEffect(() => {
    if (status !== 'user') resetChat();
  }, [status]);

  useEffect(() => {
    if (connection === 'online') retryNetworkFailed();
  }, [connection]);
  useEffect(() => {
    const on = () => retryNetworkFailed();
    window.addEventListener('online', on);
    return () => window.removeEventListener('online', on);
  }, []);

  useSocketEvent<[ChatMessageView]>('chat:message', (m) => {
    const session = useSession.getState();
    if (session.status !== 'user') return;
    receive(m);
    if (m.from === session.me?.publicId || m.text === null) return;
    if (useChat.getState().open === m.from) return;
    const show = (name: string | null) =>
      useToasts.getState().push({
        kind: 'notice',
        titleId: name === null ? 'social.badges.messageToastAnon' : 'social.badges.messageToast',
        values: { name: name ?? '', text: preview(m.text ?? '') },
        link: { to: paths.chat(l, m.from), labelId: 'social.badges.messageToastLink' },
        ttl: selectInGame(useScreen.getState()) ? MESSAGE_TOAST_GAME_MS : MESSAGE_TOAST_MS,
      });
    const known = () =>
      useChat.getState().conversations?.find((c) => c.publicId === m.from)?.nickname ?? null;
    const name = known();
    if (name !== null) return void show(name);
    // a new conversation: its name comes with the list
    void loadConversations().then(() => show(known()));
  });

  // unfriended, blocked, unblocked (by either side): who may write changed
  useSocketEvent('friends:changed', () => {
    if (useSession.getState().status !== 'user') return;
    const s = useChat.getState();
    if (s.blocked !== null) void loadBlocked();
    if (s.conversations !== null) void loadConversations();
    if (s.open !== null) void loadThread(s.open);
  });

  useSocketEvent<[ChatReadEvent]>('chat:read', (e) => {
    if (useSession.getState().status === 'user') applyRead(e);
  });

  return null;
}
